'use client';

import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Quote, User } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Testimonial {
  id: number;
  quote: string;
  name: string;
  role: string;
  company: string;
  type: string;
  category: string;
}

interface TestimonialCarouselProps {
  testimonials: Testimonial[];
  autoplaySpeed?: number;
  variant?: 'carousel' | 'grid';
}

export function TestimonialCarousel({
  testimonials,
  autoplaySpeed = 5000,
  variant = 'carousel',
}: TestimonialCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (variant === 'grid') return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, autoplaySpeed);
    return () => clearInterval(timer);
  }, [testimonials.length, autoplaySpeed, variant]);

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  if (variant === 'grid') {
    return (
      <div className="grid md:grid-cols-2 gap-6">
        {testimonials.map((t) => (
          <TestimonialCard key={t.id} testimonial={t} />
        ))}
      </div>
    );
  }

  return (
    <div className="relative max-w-4xl mx-auto">
      {/* Quote Icon */}
      <div className="flex justify-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[oklch(0.62_0.19_150)]/20 to-[oklch(0.85_0.12_140)]/10 border border-[oklch(0.62_0.19_150)]/30 flex items-center justify-center">
          <Quote className="w-6 h-6 text-[oklch(0.72_0.22_150)]" />
        </div>
      </div>

      {/* Testimonial Content */}
      <div className="relative overflow-hidden">
        <div
          className="flex transition-transform duration-700 ease-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {testimonials.map((t) => (
            <div key={t.id} className="w-full flex-shrink-0 px-4">
              <div className="text-center">
                <p className="text-xl md:text-2xl text-foreground/90 leading-relaxed mb-8 font-light italic">
                  "{t.quote}"
                </p>
                <div className="flex items-center justify-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[oklch(0.62_0.19_150)]/30 to-[oklch(0.85_0.12_140)]/20 border border-[oklch(0.62_0.19_150)]/30 flex items-center justify-center">
                    <User className="w-5 h-5 text-[oklch(0.72_0.22_150)]" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-foreground">{t.name}</p>
                    <p className="text-sm text-muted-foreground">{t.role}</p>
                  </div>
                </div>
                <div className="mt-3">
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-medium badge-glow">
                    {t.type}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-center gap-4 mt-10">
        <Button
          variant="outline"
          size="icon"
          onClick={goToPrev}
          className="w-10 h-10 rounded-full border-[oklch(0.28_0.025_170)] text-muted-foreground hover:text-[oklch(0.72_0.22_150)] hover:border-[oklch(0.62_0.19_150)]/50 bg-transparent"
          aria-label="Previous"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>

        {/* Dots */}
        <div className="flex items-center gap-2">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                i === currentIndex
                  ? 'w-8 bg-[oklch(0.62_0.19_150)] shadow-[0_0_10px_oklch(0.62_0.19_150)_/_0.5]'
                  : 'bg-[oklch(0.28_0.025_170)] hover:bg-[oklch(0.62_0.19_150)]/50'
              }`}
              aria-label={`Go to testimonial ${i + 1}`}
            />
          ))}
        </div>

        <Button
          variant="outline"
          size="icon"
          onClick={goToNext}
          className="w-10 h-10 rounded-full border-[oklch(0.28_0.025_170)] text-muted-foreground hover:text-[oklch(0.72_0.22_150)] hover:border-[oklch(0.62_0.19_150)]/50 bg-transparent"
          aria-label="Next"
        >
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );
}

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <div className="card-glow rounded-2xl p-7 h-full flex flex-col">
      <Quote className="w-8 h-8 text-[oklch(0.62_0.19_150)]/30 mb-4" />
      <p className="text-foreground/90 leading-relaxed mb-6 flex-grow">
        "{testimonial.quote}"
      </p>
      <div className="flex items-center gap-4 pt-5 border-t border-[oklch(0.28_0.025_170)]/50">
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[oklch(0.62_0.19_150)]/30 to-[oklch(0.85_0.12_140)]/20 border border-[oklch(0.62_0.19_150)]/30 flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5 text-[oklch(0.72_0.22_150)]" />
        </div>
        <div>
          <p className="font-semibold text-foreground text-sm">{testimonial.name}</p>
          <p className="text-xs text-muted-foreground">{testimonial.role}</p>
          <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium badge-glow">
            {testimonial.type}
          </span>
        </div>
      </div>
    </div>
  );
}
